/**
 * API client.
 *
 * Every request carries the session cookie and the X-Requested-With header the
 * server requires on writes. A 401 means the session ended (expired, revoked, or
 * the account was deactivated), which the app handles globally by returning to
 * the login screen rather than each caller handling it.
 */

export class ApiError extends Error {
  constructor(status, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details || null;
  }
}

const unauthorizedListeners = new Set();

/** Registers a callback fired whenever any request returns 401. */
export function onUnauthorized(listener) {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

async function request(method, path, { body, formData, signal } = {}) {
  const headers = { 'X-Requested-With': 'governor-app' };
  let payload;

  if (formData) {
    // Let the browser set the multipart boundary.
    payload = formData;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: payload,
      credentials: 'same-origin',
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ApiError(0, 'تعذّر الاتصال بالخادم. تحقّق من اتصال الشبكة.');
  }

  if (response.status === 204) return null;

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      for (const listener of unauthorizedListeners) listener();
    }
    throw new ApiError(
      response.status,
      data?.error || 'حدث خطأ أثناء تنفيذ الطلب',
      data?.details
    );
  }

  return data;
}

export const api = {
  /* Authentication */
  login: (username, password) =>
    request('POST', '/api/auth/login', { body: { username, password } }),
  loginWithDisplayToken: (token) =>
    request('POST', '/api/auth/display', { body: { token } }),
  logout: () => request('POST', '/api/auth/logout'),
  me: () => request('GET', '/api/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    request('POST', '/api/auth/password', { body: { currentPassword, newPassword } }),

  /* Appointments */
  getMeetingsByDate: (date, options) =>
    request('GET', `/api/meetings?date=${encodeURIComponent(date)}`, options),
  getMeetingsByRange: (from, to, options) =>
    request(
      'GET',
      `/api/meetings?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      options
    ),
  createMeeting: (meeting) => request('POST', '/api/meetings', { body: meeting }),
  updateMeeting: (id, meeting) =>
    request('PUT', `/api/meetings/${encodeURIComponent(id)}`, { body: meeting }),
  setMeetingStatus: (id, status) =>
    request('PATCH', `/api/meetings/${encodeURIComponent(id)}/status`, {
      body: { status },
    }),
  deleteMeeting: (id) => request('DELETE', `/api/meetings/${encodeURIComponent(id)}`),

  /* Settings */
  getSettings: () => request('GET', '/api/settings'),
  updateSettings: (patch) => request('PUT', '/api/settings', { body: patch }),
  uploadLetterhead: (file) => {
    const formData = new FormData();
    formData.append('letterhead', file);
    return request('POST', '/api/settings/letterhead', { formData });
  },
  deleteLetterhead: () => request('DELETE', '/api/settings/letterhead'),

  /* Users */
  getUsers: () => request('GET', '/api/users'),
  createUser: (user) => request('POST', '/api/users', { body: user }),
  updateUser: (id, patch) => request('PUT', `/api/users/${id}`, { body: patch }),
  deleteUser: (id) => request('DELETE', `/api/users/${id}`),

  /* Display links */
  getDisplayTokens: () => request('GET', '/api/display-tokens'),
  createDisplayToken: (label, userId) =>
    request('POST', '/api/display-tokens', { body: { label, userId } }),
  revokeDisplayToken: (token) =>
    request('DELETE', `/api/display-tokens/${encodeURIComponent(token)}`),
};

export const LETTERHEAD_URL = '/api/settings/letterhead/image';

export default api;
